import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Menus, Text, Tooltip } from '@Sage/hoi-poi-ui';

import AutomationBuilder from 'containers/settings/AutomationSettings/components/AutomationBuilder';
import { AUTOMATIONS_SETTINGS } from 'constants/Features';
import { getLiteral } from 'utils/getLiteral';
import { ensureRoute } from 'utils/routes';
import { logEvent } from 'utils/tracking';

import AutomationTemplatesModal from './AutomationTemplatesModal';

import './styles.scss';

const AutomationEntityMenu = memo(({ onRef, buttonClassName, entity }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const builderRef = useRef(null);

    const isAdmin = useSelector((state) => state.config?.permission?.canConfigImplementation);
    const automationsSettings = useSelector(
        (state) => state.config?.flagsmith?.[AUTOMATIONS_SETTINGS],
    );
    const automationsEnabled = useSelector((state) => state.config?.userData?.automationsEnabled);

    const isAllowed = useMemo(
        () => isAdmin && (automationsSettings || automationsEnabled),
        [isAdmin, automationsSettings, automationsEnabled],
    );

    const onBuilderRef = useCallback((ref) => {
        builderRef.current = ref;
    }, []);

    useEffect(() => {
        onRef &&
            onRef({
                open(...args) {
                    setIsModalOpen(true);
                    if (args && args.length) {
                        setTimeout(() => builderRef.current?.open(...args), 0);
                    }
                },
                close() {
                    setIsModalOpen(false);
                },
            });
    }, [onRef]);

    // ========================================
    // MENU ACTIONS
    // ========================================

    const openTemplatesModal = useCallback(() => {
        setIsModalOpen(true);
        logEvent({
            event: entity?.trueName || 'settings',
            submodule: 'automationsMenu',
            functionality: 'openTemplates',
        });
    }, [entity?.trueName]);

    const createFromScratch = useCallback(() => {
        builderRef.current?.open();
        logEvent({
            event: entity?.trueName || 'settings',
            submodule: 'automationsMenu',
            functionality: 'createFromScratch',
        });
    }, [entity?.trueName]);

    const manageAutomations = useCallback(() => {
        ensureRoute('/settings/automations');
        logEvent({
            event: entity?.trueName || 'settings',
            submodule: 'automationsMenu',
            functionality: 'manageAutomations',
        });
    }, [entity?.trueName]);

    const openBuilder = useCallback((...args) => {
        setTimeout(() => builderRef.current?.open(...args), 0);
    }, []);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    // ========================================
    // MEMOIZED VALUES
    // ========================================

    const menuItems = useMemo(
        () => [
            {
                infoItem: true,
                title: (
                    <Text bold type="body">
                        {getLiteral('label_automations')}
                    </Text>
                ),
            },
            {
                onClick: openTemplatesModal,
                title: getLiteral('action_automation_create_from_template'),
            },
            {
                onClick: createFromScratch,
                title: getLiteral('action_automation_create'),
            },
            {
                onClick: manageAutomations,
                title: getLiteral('action_automation_manage'),
            },
        ],
        [createFromScratch, manageAutomations, openTemplatesModal],
    );

    const triggerButton = useMemo(
        () => (
            <Tooltip content={getLiteral('label_automations')}>
                <Button className={buttonClassName} icon="automations" type="terciary" />
            </Tooltip>
        ),
        [buttonClassName],
    );

    if (!isAllowed) return null;

    return (
        <>
            <Menus
                className="fm-automation-entity-menu__dropdown"
                items={menuItems}
                title={triggerButton}
            />
            <AutomationTemplatesModal
                entity={entity}
                isOpen={isModalOpen}
                onClose={closeModal}
                openBuilder={openBuilder}
            />
            <AutomationBuilder onRef={onBuilderRef} />
        </>
    );
});

AutomationEntityMenu.displayName = 'AutomationEntityMenu';

export default AutomationEntityMenu;
